import axios from "axios";
import { invalidate, TAGS } from "./liveSync";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ============================================================================
// Sliding refresh (Iter 19) — "Beni Hatırla" ile giren operator için token
// süresi bitmeden önce arka planda yenilenir. Auth Context'te set edilen
// `otopark_remember` flag'i varsa ve token < %20 kaldıysa /auth/refresh çağrılır.
// Böylece sabit cihazdan çalışan operatör tekrar şifre girmez.
// ============================================================================
let _refreshing = null;
const parseJwtExp = (tok) => {
  try {
    const payload = JSON.parse(atob(tok.split(".")[1]));
    return { exp: payload.exp * 1000, iat: (payload.iat || 0) * 1000, role: payload.role };
  } catch { return null; }
};
async function _maybeRefresh() {
  const remember = localStorage.getItem("otopark_remember") === "1";
  const tok = localStorage.getItem("otopark_token");
  if (!remember || !tok) return;
  const info = parseJwtExp(tok);
  if (!info || info.role !== "operator") return;
  const now = Date.now();
  const life = info.exp - info.iat;
  const remain = info.exp - now;
  // Kalan süre TTL'in %20'sinden az ise ve pozitifse yenile
  if (remain > 0 && remain < life * 0.2) {
    if (_refreshing) return _refreshing;
    _refreshing = axios.post(`${API}/auth/refresh`, {}, {
      headers: { Authorization: `Bearer ${tok}` },
      withCredentials: true,
    })
      .then((r) => {
        if (r.data?.token) localStorage.setItem("otopark_token", r.data.token);
      })
      .catch(() => { /* geçersiz — sonra 401 alacak zaten */ })
      .finally(() => { _refreshing = null; });
    return _refreshing;
  }
}

api.interceptors.request.use(async (config) => {
  await _maybeRefresh();  // token bitmek üzereyse yenile
  const token = localStorage.getItem("otopark_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Map API URL patterns → invalidation tag(s) so that any successful
 * mutation (POST/PUT/PATCH/DELETE) triggers refresh of related pages.
 * Ordered: first matching entry wins.
 */
const INVALIDATION_RULES = [
  { pattern: /\/plans(\/|$)/, tags: [TAGS.PLANS, TAGS.PLATE_SUBSCRIPTIONS, TAGS.SUBSCRIPTION_PAYMENTS] },
  { pattern: /\/plate-subscriptions/, tags: [TAGS.PLATE_SUBSCRIPTIONS, TAGS.SUBSCRIPTION_PAYMENTS] },
  { pattern: /\/subscription-payments/, tags: [TAGS.SUBSCRIPTION_PAYMENTS, TAGS.PLATE_SUBSCRIPTIONS, TAGS.KASA] },
  { pattern: /\/tariffs/, tags: [TAGS.TARIFFS, TAGS.SESSIONS] },
  { pattern: /\/sessions\/[^/]+\/(pay|waive)/, tags: [TAGS.SESSIONS, TAGS.UNPAID, TAGS.KASA, TAGS.PAYMENTS_LEDGER, TAGS.DASHBOARD] },
  { pattern: /\/discounts\//, tags: [TAGS.SESSIONS, TAGS.UNPAID, TAGS.PAYMENTS_LEDGER, TAGS.KASA, TAGS.DASHBOARD] },
  { pattern: /\/coupons/, tags: [TAGS.COUPONS] },
  { pattern: /\/coupons\/apply/, tags: [TAGS.COUPONS, TAGS.SESSIONS, TAGS.UNPAID] },
  { pattern: /\/sessions(\/|\?|$)/, tags: [TAGS.SESSIONS, TAGS.UNPAID, TAGS.KASA, TAGS.PAYMENTS_LEDGER, TAGS.DASHBOARD] },
  { pattern: /\/unmatched-exits/, tags: [TAGS.UNMATCHED, TAGS.SESSIONS] },
  { pattern: /\/access-list/, tags: [TAGS.ACCESS] },
  { pattern: /\/users/, tags: [TAGS.USERS] },
  { pattern: /\/cameras/, tags: [TAGS.CAMERAS, TAGS.PARKING_LOTS] },
  { pattern: /\/parking-lots/, tags: [TAGS.PARKING_LOTS] },
  { pattern: /\/corporate-accounts/, tags: [TAGS.CORPORATE] },
  { pattern: /\/settings/, tags: [TAGS.SETTINGS] },
];

api.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || "get").toLowerCase();
    if (method !== "get") {
      const url = response.config?.url || "";
      for (const rule of INVALIDATION_RULES) {
        if (rule.pattern.test(url)) {
          invalidate(rule.tags);
          break;
        }
      }
    }
    return response;
  },
  (error) => {
    // Pydantic 422 validation errors return `detail: [{type, loc, msg, ...}]`.
    // Rendering that array directly in a toast → React "Objects are not valid
    // as a React child" crash. Stringify HERE so every caller stays safe.
    try {
      const d = error?.response?.data?.detail;
      if (Array.isArray(d)) {
        error.response.data.detail = d
          .map((e) => (e && typeof e === "object" ? (e.msg || JSON.stringify(e)) : String(e)))
          .join(" · ");
      } else if (d && typeof d === "object") {
        error.response.data.detail = d.msg || JSON.stringify(d);
      }
    } catch { /* noop */ }
    return Promise.reject(error);
  },
);

export default api;

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Bir hata oluştu";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d);
}

export function formatTL(v) {
  const n = Number(v || 0);
  return `₺${n.toFixed(2)}`;
}

export function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function formatDuration(minutes) {
  const m = Math.max(0, Math.floor(minutes || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}sa ${mm}dk` : `${mm}dk`;
}

export function formatDurationDays(minutes) {
  const m = Math.max(0, Math.floor(minutes || 0));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return `${d} gün ${h} sa`;
}

export function isMultiDay(minutes) {
  return (minutes || 0) >= 1440;
}
